import { Link, createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError, authApi } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>) => {
    const token = typeof search["token"] === "string" ? search["token"] : "";
    return { token };
  },
  head: () => ({
    meta: [
      { title: "Choose a new password" },
      { name: "description", content: "Set a new password for your resident account." },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const { platformName } = usePlatformConfig();
  const search = Route.useSearch();
  const [token, setToken] = useState(search.token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("auth.passwordShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token.trim(), password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.errorGeneric"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {t("auth.forgotPassword")}
            </p>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold">{t("auth.resetTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.resetBody")}</p>

        {done ? (
          <p className="mt-6 rounded-md border border-primary/20 bg-primary-soft px-3 py-2 text-sm">
            {t("auth.resetDone")}
          </p>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <label className="block text-xs font-semibold">
              {t("auth.resetCode")}
              <input
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="one-time-code"
              />
            </label>
            <label className="block text-xs font-semibold">
              {t("auth.newPassword")}
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="new-password"
              />
            </label>
            <label className="block text-xs font-semibold">
              {t("auth.confirmPassword")}
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="new-password"
              />
            </label>
            {error ? (
              <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={loading || token.trim().length < 20}>
              {loading ? t("common.loading") : t("auth.resetCta")}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            {t("auth.backToLogin")}
          </Link>
        </p>
      </div>
    </div>
  );
}
